import React, { useEffect, useState, useCallback } from 'react';
import InstallerLayout from '../components/InstallerLayout';
import api from '../api/axios';
import { C } from '../theme';

const REQ = {
  pending:  { bg: '#FEF3C7', color: '#92400E', label: 'Awaiting admin' },
  approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
  rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
};

export default function InstallerMapping() {
  const [packages, setPackages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [apps,     setApps]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('map');   // 'map' | 'requests'
  const [toast,    setToast]    = useState(null);

  // form
  const [sel,     setSel]     = useState(null);   // selected package
  const [form,    setForm]    = useState({ client_id: '', application_id: '', ref_number: '', notes: '' });
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [cliQuery, setCliQuery] = useState('');   // client search box
  const [cliOpen,  setCliOpen]  = useState(false);

  const show = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, c, a] = await Promise.all([
        api.get('/api/installer/my-packages'),
        api.get('/api/mapping-requests'),
        api.get('/api/clients'),
        api.get('/api/applications').catch(() => ({ data: [] })),
      ]);
      setPackages(p.data); setRequests(r.data); setClients(c.data); setApps(a.data || []);
    } catch { show('error', 'Could not load your devices.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openMap = (pkg) => {
    setSel(pkg);
    setForm({ client_id: '', application_id: '', ref_number: '', notes: '' });
    setCliQuery(''); setCliOpen(false);
    setErr('');
  };

  const submit = async () => {
    if (!form.client_id)        { setErr('Select the client this is installed for.'); return; }
    if (!form.application_id)   { setErr('Select the application / vehicle.'); return; }
    if (!form.ref_number.trim()) { setErr('Application number is required.'); return; }
    setSaving(true); setErr('');
    try {
      await api.post('/api/installer/mapping-requests', {
        client_id:      Number(form.client_id),
        package_id:     sel.package_db_id,
        application_id: Number(form.application_id),
        ref_number:     form.ref_number.trim(),
        notes:          form.notes.trim() || null,
      });
      setSel(null);
      await load();
      setTab('requests');
      show('success', 'Sent to admin for pricing & confirmation.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not submit.');
    } finally { setSaving(false); }
  };

  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <InstallerLayout title="Mapping">
      {toast && (
        <div style={{
          position: 'fixed', top: '18px', right: '20px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border: `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '11px 18px', fontSize: '13px', fontWeight: 600,
        }}>{toast.type === 'success' ? '✓' : '⚠'} {toast.text}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px', marginBottom: '18px', width: 'fit-content' }}>
        {[
          { k: 'map',      l: `📦 Devices in Hand (${packages.length})` },
          { k: 'requests', l: `📨 My Requests${pending ? ` (${pending})` : ''}` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            fontSize: '13px', fontWeight: 700,
            background: tab === t.k ? C.white : 'transparent',
            color: tab === t.k ? C.textMain : C.textSub,
            boxShadow: tab === t.k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
          }}>{t.l}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '50px', textAlign: 'center', color: C.textSub, fontSize: '13px' }}>Loading…</div>
      ) : tab === 'map' ? (
        packages.length === 0 ? (
          <Empty icon="📦" title="No devices in your hand"
            text="Scan a package in Get Device to receive it, then map it to a client here." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
            {packages.map(p => (
              <div key={p.package_db_id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                <div style={{ padding: '13px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 800, color: C.primaryDeep }}>{p.package_ref}</div>
                    {p.package_name && <div style={{ fontSize: '11px', color: C.textSub }}>{p.package_name}</div>}
                  </div>
                  <span style={{ fontSize: '11px', color: C.textSub }}>{p.items.length} device{p.items.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ padding: '11px 16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {p.items.map(it => (
                    <div key={it.inv_db_id} style={{ fontSize: '12px' }}>
                      <span style={{ fontWeight: 700, color: C.textMain }}>{it.category_name}</span>
                      {it.type_name && <span style={{ color: C.textSub }}> · {it.type_name}</span>}
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>
                        {it.serial_number || it.item_id}{it.ref_number ? ` · ${it.ref_number}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '11px 16px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA' }}>
                  <button onClick={() => openMap(p)} style={{
                    width: '100%', padding: '9px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff',
                    fontSize: '13px', fontWeight: 700,
                  }}>🗺️ Map to Client</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        requests.length === 0 ? (
          <Empty icon="📨" title="No requests yet" text="Map a device to a client and it will show here." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            {requests.map(r => {
              const st = REQ[r.status] || REQ.pending;
              return (
                <div key={r.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: C.primaryDeep }}>{r.request_id}</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{r.client_name}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{r.package_ref}</span>
                      <span style={{ background: st.bg, color: st.color, padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{st.label}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: C.textSub }}>{r.created_at}</span>
                  </div>
                  {r.status === 'approved' && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#065F46' }}>
                      ✓ Confirmed as <strong>{r.mapping_ref}</strong> · priced ₹{(r.total_amount || 0).toLocaleString('en-IN')}
                      {r.total_amc > 0 && ` + AMC ₹${r.total_amc.toLocaleString('en-IN')}`}
                    </div>
                  )}
                  {r.status === 'rejected' && r.admin_notes && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#991B1B' }}>✕ {r.admin_notes}</div>
                  )}
                  {r.notes && <div style={{ marginTop: '6px', fontSize: '11px', color: C.textSub, fontStyle: 'italic' }}>“{r.notes}”</div>}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Map modal ── */}
      {sel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '460px', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '17px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: C.textMain }}>Map to Client</div>
                <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{sel.package_ref} · {sel.items.length} device(s)</div>
              </div>
              <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', fontSize: '19px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              <Field label="Client *">
                {(() => {
                  const selected = clients.find(c => String(c.id) === String(form.client_id));
                  const q = cliQuery.trim().toLowerCase();
                  const matches = !q ? clients : clients.filter(c =>
                    [c.client_name, c.client_id, c.contact, c.company_name]
                      .some(v => v?.toLowerCase?.().includes(q)));
                  return (
                    <div style={{ position: 'relative' }}>
                      <input
                        value={cliOpen ? cliQuery : (selected ? `${selected.client_name} · ${selected.contact || selected.client_id}` : '')}
                        onChange={e => { setCliQuery(e.target.value); setCliOpen(true); setForm(f => ({ ...f, client_id: '' })); setErr(''); }}
                        onFocus={() => { setCliOpen(true); setCliQuery(''); }}
                        onBlur={() => setTimeout(() => setCliOpen(false), 150)}
                        placeholder="🔍 Search client by name or mobile…"
                        style={inp}
                      />
                      {form.client_id && !cliOpen && (
                        <button onClick={() => { setForm(f => ({ ...f, client_id: '' })); setCliQuery(''); }}
                          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textSub, fontSize: '14px' }}>✕</button>
                      )}
                      {cliOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: C.white, border: `1.5px solid ${C.border}`, borderRadius: '9px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', maxHeight: '220px', overflowY: 'auto' }}>
                          {matches.length === 0 ? (
                            <div style={{ padding: '12px 14px', fontSize: '12px', color: C.textSub }}>No client matches “{cliQuery}”.</div>
                          ) : matches.slice(0, 40).map(c => (
                            <div key={c.id}
                              onMouseDown={() => { setForm(f => ({ ...f, client_id: String(c.id) })); setCliOpen(false); setCliQuery(''); setErr(''); }}
                              style={{ padding: '9px 13px', cursor: 'pointer', borderBottom: `1px solid ${C.primaryBg}` }}
                              onMouseEnter={e => e.currentTarget.style.background = C.primaryBg}
                              onMouseLeave={e => e.currentTarget.style.background = C.white}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{c.client_name}</div>
                              <div style={{ fontSize: '11px', color: C.textSub }}>
                                📞 {c.contact || '—'} · <span style={{ fontFamily: 'monospace' }}>{c.client_id}</span>
                                {c.company_name ? ` · ${c.company_name}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Field>
              <Field label="Application / Vehicle *">
                <select value={form.application_id} onChange={e => { setForm(f => ({ ...f, application_id: e.target.value })); setErr(''); }} style={inp}>
                  <option value="">— Select application —</option>
                  {apps.map(a => (
                    <option key={a.id} value={a.id}>
                      {[a.vehicle_type, a.make, a.model].filter(Boolean).join(' ')} {a.registration_number ? `· ${a.registration_number}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Application Number *">
                <input value={form.ref_number} onChange={e => { setForm(f => ({ ...f, ref_number: e.target.value })); setErr(''); }} placeholder="Enter application number" style={inp} />
              </Field>
              <Field label="Notes for admin">
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  placeholder="Anything the admin should know…" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>

              <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '9px', padding: '10px 13px', fontSize: '11px', color: '#5B21B6', lineHeight: 1.5 }}>
                ℹ You don't set the price. Admin reviews this, fixes the amount, and confirms the mapping.
              </div>
              {err && <div style={{ marginTop: '10px', color: C.danger, fontSize: '12px', fontWeight: 600 }}>{err}</div>}
            </div>

            <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button onClick={() => setSel(null)} disabled={saving} style={{ padding: '9px 17px', background: '#F3F4F6', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submit} disabled={saving} style={{ padding: '9px 22px', background: saving ? '#9CA3AF' : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Sending…' : '📨 Send to Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </InstallerLayout>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '13px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>{label}</label>
      {children}
    </div>
  );
}

function Empty({ icon, title, text }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>{icon}</div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>{title}</div>
      <div style={{ fontSize: '12px', color: C.textSub }}>{text}</div>
    </div>
  );
}

const inp = {
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px',
  color: C.textMain, outline: 'none', background: C.white,
};
